import type {
    ResultsAreaSlice,
    ResultsAreaSliceConfig,
    ResultsAreaSliceStatus,
    ResultsAreaPersistedState,
    ResultsAreaIdeaMode,
    ResultsAreaSliceEventBus,
} from '.';
import type { ColumnOptionsSliceEventBus } from '../ColumnOptionsSlice';
import type { StickyFilterSliceEventBus } from '../StickyFilterSlice';
import { Duration } from '@js-joda/core';
import { computed, effect, signal, untracked } from '@preact/signals-react';
import type { ReadonlySignal, Signal } from '@preact/signals-react';
import type { QueryClient } from '@tanstack/query-core';
import type { OutputColumnWithMetadata, SortModel } from '@thinkalpha/platform-ws-client/contracts/table.js';
import type { Universe } from '@thinkalpha/platform-ws-client/contracts/universe.js';
import { DisposableBox } from '@thinkalpha/ui-core/DisposableBox';
import { shortid } from '@thinkalpha/ui-core/shortid';
import type { ColumnTemplate, KnownColumnTemplateKey } from '@thinkalpha/ui-types/contracts/column-template';
import { isEqual } from 'es-toolkit';
import { t } from 'i18next';
import { optional } from 'inversify';
import type { IdeaDTO } from 'src/dtos/Idea';
import { inject, injectable } from 'src/features/ioc';
import {
    filterableTemplateColumns,
    searchWithColumnFilters,
} from 'src/features/table/columnFilters/columnFilterFormula';
import {
    buildResultsTableQueryOptions,
    clientSortModelToSortModel,
} from 'src/features/table/hooks/useResultsTableQueryOptions';
import { FullTableExport } from 'src/features/table/stitching/FullTableExport';
import type { StitchedColumns } from 'src/features/table/stitching/StitchedColumns';
import type { StitchedColumnsFactory } from 'src/features/table/stitching/stitchedColumnsFactory';
import { SymbolTableStitching } from 'src/features/table/stitching/SymbolTableStitching';
import type { ClientSortModel } from 'src/features/table/types';
import { UniversePlanDTO } from 'src/features/universe/dtos/UniversePlanDTO';
import { enableStitchedSymbolTables } from 'src/lib/featureFlags/flags/enableStitchedSymbolTables';
import { enableTableColumnFilters } from 'src/lib/featureFlags/flags/enableTableColumnFilters';
import { exportTableToCSV, exportWithToasts } from 'src/lib/util/exportTableCSV';
import type { ExportTableCSVResult } from 'src/lib/util/exportTableCSV';
import { ColumnFiltersModel } from 'src/models/ColumnFiltersModel';
import { tableColumnFilters } from 'src/models/ColumnFiltersModel/tableColumnFilters';
import type { TableColumnFilters } from 'src/models/ColumnFiltersModel/tableColumnFilters';
import type { QueryObserverModel } from 'src/models/QueryObserverModel';
import type { SlowIdeasSelector } from 'src/models/SlowIdeasSelector';
import type { StoreAdapter } from 'src/models/StoreAdapter';
import type { TableModel } from 'src/models/TableModel';
import { getColumnTemplateByIdQuery, getDefaultColumnTemplateQuery } from 'src/queries/columnTemplates';
import { getIdeaDTOByIdQuery } from 'src/queries/ideas';
import { getUniversesQuery } from 'src/queries/universes';
import { generateWorkableColumnTemplateGridSettings } from 'src/routes/dashboard/widget-container/widget-settings-editor/context';
import type { FeatureFlagService } from 'src/services/FeatureFlagService';
import type { FormulaService } from 'src/services/FormulaService';
import type { SlugMapService } from 'src/services/SlugMapService';
import type { ToastService } from 'src/services/ToastService';
import { makeColumnTemplateEqualityKey } from 'src/types';
import type { FetchableTableCreationResultWithColumns } from 'src/types';
import type { ReactBindings } from 'src/types/bindings';
import { EventBusBuilder } from 'src/types/EventBus/EventBus';
import { v4 } from 'uuid';

@injectable()
export class ResultsAreaSliceImpl implements ResultsAreaSlice {
    #disposableStack = new DisposableStack();

    // Exposed state signals
    #status: Signal<ResultsAreaSliceStatus> = signal('pending');
    #error: Signal<Error | null> = signal(null);
    #columnTemplate: Signal<ColumnTemplate | null> = signal(null);
    #fastIdeas: Signal<IdeaDTO[]> = signal([]);
    #slowIdea: ReadonlySignal<IdeaDTO | null> = signal(null);
    #widgetPlanIdea: Signal<IdeaDTO | null> = signal(null);
    #validationColumns: Signal<OutputColumnWithMetadata[]> = signal([]);
    #clientSortModel: Signal<ClientSortModel> = signal([]);

    // Config signals (from store subscriptions or config signals)
    #scrollingEnabled: Signal<boolean> = signal(false);
    #filterSignal!: ReadonlySignal<string>;
    #columnOptionsEventBus!: ColumnOptionsSliceEventBus;
    #validationModeEnabled!: ReadonlySignal<boolean>;
    #dwellTimeSignal!: ReadonlySignal<number>;
    #userViewKeySignal!: ReadonlySignal<string | undefined>;
    #incomingColumnTemplate: Signal<ColumnTemplate | string | null> = signal(null);
    #universeId: Signal<string | null> = signal(null);

    // Cache key signals for memoization
    #fastIdeasCacheKey: Signal<string> = signal(v4());
    #slowIdeaCacheKey: ReadonlySignal<string> = signal(v4());
    #widgetPlanCacheKey: Signal<string> = signal(v4());

    // Memoized derived values
    #sort: Signal<SortModel | undefined> = signal(undefined);
    /** Client stitching for the table (ADR 0020); created with the table query. */
    #stitched: Signal<SymbolTableStitching | null> = signal(null);
    #stitching: ReadonlySignal<StitchedColumns | undefined> = computed(() => this.#stitched.value?.columns.value);

    // For tracking enabled state changes (for logging)
    #prevEnabled: boolean | undefined = undefined;

    // Internal state
    #columnTemplateKey: KnownColumnTemplateKey | null = null;
    #devtoolsName: string | undefined;
    #columnTemplateQuery: QueryObserverModel<ColumnTemplate, unknown, ColumnTemplate> | null = null;
    #universesQuery: QueryObserverModel<Universe[], unknown, Universe[]> | null = null;
    #slowIdeaQuery: QueryObserverModel<IdeaDTO, unknown, IdeaDTO> | null = null;
    #ideaMode: ResultsAreaIdeaMode = 'standard';
    #ideaUserViewKeyScope!: ResultsAreaSliceConfig['ideaUserViewKeyScope'];
    #onUserSelectedSymbol: ((symbol: string) => void) | null = null;

    readonly #log;
    readonly #queryClient: QueryClient;
    readonly #formulaService: FormulaService;
    readonly #slugMapService: SlugMapService;
    readonly #queryObserverModelFactory: ReactBindings['QueryObserverModelFactory'];
    readonly #table: TableModel;
    readonly #featureFlagService: FeatureFlagService;
    readonly #createTable: ReactBindings['TableModelFactory'];
    readonly #toasts: ToastService;
    readonly #stitchedColumnsFactory: StitchedColumnsFactory | undefined;
    /** The export of a stitched table in progress; a new export supersedes it. */
    readonly #fullTableExport = this.#disposableStack.use(new DisposableBox<FullTableExport>());

    readonly eventBus: ResultsAreaSliceEventBus;
    // Session-only (ADR 0014, AC-FILTER-004): never written through the store adapter.
    readonly columnFilterState = this.#disposableStack.use(new ColumnFiltersModel());
    readonly columnFilters: TableColumnFilters;
    readonly #filterableColumns = computed(() => filterableTemplateColumns(this.#columnTemplate.value?.columns ?? []));

    constructor(
        @inject('QueryClient') queryClient: QueryClient,
        @inject('FormulaService') formulaService: FormulaService,
        @inject('SlugMapService') slugMapService: SlugMapService,
        @inject('QueryObserverModelFactory')
        queryObserverModelFactory: ReactBindings['QueryObserverModelFactory'],
        @inject('TableModel') table: TableModel,
        @inject('Logger') logger: ReactBindings['Logger'],
        @inject('FeatureFlagService') featureFlagService: FeatureFlagService,
        @inject('TableModelFactory') createTable: ReactBindings['TableModelFactory'],
        @inject('ToastService') toasts: ToastService,
        @inject('StitchedColumnsFactory') @optional() stitchedColumnsFactory?: StitchedColumnsFactory,
    ) {
        this.#queryClient = queryClient;
        this.#formulaService = formulaService;
        this.#slugMapService = slugMapService;
        this.#queryObserverModelFactory = queryObserverModelFactory;
        this.#table = table;
        this.#featureFlagService = featureFlagService;
        this.#createTable = createTable;
        this.#toasts = toasts;
        this.#stitchedColumnsFactory = stitchedColumnsFactory;
        this.#log = logger.getSubLogger({ name: `ResultsAreaSlice:${shortid()}` }, { component: 'table' });
        this.columnFilters = tableColumnFilters(this.columnFilterState, {
            filterableColumns: this.#filterableColumns,
            enabled: featureFlagService.getSignal(enableTableColumnFilters),
        });

        this.eventBus = EventBusBuilder.create()
            .addSignal('widget::isScrollingTable', () => this.#table.isScrolling)
            .addSignal('widget::validationColumns', () => this.#validationColumns)
            .addSignal('widget::clientSortModel', () => this.#clientSortModel)
            .addEvent('widget::setClientSortModel', (payload) => {
                this.#clientSortModel.value = payload.sortModel;
            })
            .addEvent('widget::instrumentChange', (payload) => {
                this.#onUserSelectedSymbol?.(payload.instrument);
            })
            .build();
    }

    // Public readonly signals
    get status(): ReadonlySignal<ResultsAreaSliceStatus> {
        return this.#status;
    }

    get error(): ReadonlySignal<Error | null> {
        return this.#error;
    }

    get table(): TableModel {
        return this.#table;
    }

    get columnTemplate(): ReadonlySignal<ColumnTemplate | null> {
        return this.#columnTemplate;
    }

    get fastIdeas(): ReadonlySignal<IdeaDTO[]> {
        return this.#fastIdeas;
    }

    get slowIdea(): ReadonlySignal<IdeaDTO | null> {
        return this.#slowIdea;
    }

    get widgetPlanIdea(): ReadonlySignal<IdeaDTO | null> {
        return this.#widgetPlanIdea;
    }

    get validationColumns(): ReadonlySignal<OutputColumnWithMetadata[]> {
        return this.#validationColumns;
    }

    get clientSortModel(): ReadonlySignal<ClientSortModel> {
        return this.#clientSortModel;
    }

    get stitching(): ReadonlySignal<StitchedColumns | undefined> {
        return this.#stitching;
    }

    init<T extends ResultsAreaPersistedState>(adapter: StoreAdapter<T>, config: ResultsAreaSliceConfig): void {
        this.#status.value = 'loading';
        this.#columnTemplateKey = config.columnTemplateKey;
        this.#ideaMode = config.ideaMode;
        this.#ideaUserViewKeyScope = config.ideaUserViewKeyScope;
        this.#devtoolsName = config.devtoolsName;
        this.#onUserSelectedSymbol = config.onUserSelectedSymbol;

        // 1. Setup store subscriptions and signal references
        this.#columnOptionsEventBus = config.columnOptionsEventBus;
        this.#validationModeEnabled = config.validationModeSignal;
        this.#dwellTimeSignal = config.dwellTimeSignal ?? signal(0);
        this.#userViewKeySignal = config.userViewKeySignal;
        this.#setupStoreSubscriptions(adapter, config.stickyFilterEventBus);

        // 2. Setup column template loading
        this.#setupColumnTemplateQuery(config.columnTemplateKey);

        // 3. Setup ideas loading based on mode
        const useUniverseResolution = config.ideaMode === 'scanner' || config.resolveUniverseId === true;
        if (useUniverseResolution && config.slowIdeasSelector) {
            this.#setupUniverseResolvedSlowIdeaLoading(config.slowIdeasSelector);
        } else if (config.ideaMode === 'standard') {
            if (config.slowIdeasSelector) {
                this.#setupStandardSlowIdeasLoading(config.slowIdeasSelector);
            }
        }

        // 4. Setup reactive table query
        this.#setupTableQuery();

        // 5. Re-bump the widget-plan cache key whenever the slug map signal
        //    emits (e.g. after a subscription change invalidates the slug map
        //    query). Skipping the first emission so initial-load doesn't bump
        //    redundantly. Combined with the DTO refactor, this lets the table
        //    query re-run with the fresh slug map without a page refresh.
        let widgetPlanSlugMapSeen = false;
        this.#disposableStack.defer(
            effect(() => {
                void this.#slugMapService.slugMap.value;
                if (!widgetPlanSlugMapSeen) {
                    widgetPlanSlugMapSeen = true;
                    return;
                }
                this.#widgetPlanCacheKey.value = v4();
            }),
        );
    }

    setWidgetPlanIdea(idea: IdeaDTO | null): void {
        if (idea !== this.#widgetPlanIdea.value) {
            this.#widgetPlanIdea.value = idea;
            this.#widgetPlanCacheKey.value = v4();
        }
    }

    setFastIdeas(ideas: IdeaDTO[]): void {
        const currentIdeas = this.#fastIdeas.value;
        const ideasChanged =
            ideas.length !== currentIdeas.length || ideas.some((idea, index) => idea !== currentIdeas[index]);

        if (ideasChanged) {
            this.#fastIdeas.value = ideas;
            this.#fastIdeasCacheKey.value = v4();
        }
    }

    #setupStoreSubscriptions<T extends ResultsAreaPersistedState>(
        adapter: StoreAdapter<T>,
        stickyFilterEventBus: StickyFilterSliceEventBus,
    ): void {
        // Subscribe to column template changes from adapter
        this.#disposableStack.defer(
            adapter.subscribe((state) => {
                const ct = state.columnTemplate;
                if (ct !== this.#incomingColumnTemplate.value) {
                    this.#incomingColumnTemplate.value = ct;
                }
            }),
        );
        // Set initial value
        this.#incomingColumnTemplate.value = adapter.getState().columnTemplate;

        // Subscribe to scrolling changes from adapter
        this.#disposableStack.defer(
            adapter.subscribe((state) => {
                this.#scrollingEnabled.value = state.scrolling;
            }),
        );
        this.#scrollingEnabled.value = adapter.getState().scrolling;

        // Subscribe to universeId changes from adapter (for scanner slow idea loading)
        this.#disposableStack.defer(
            adapter.subscribe((state) => {
                this.#universeId.value = state.universeId;
            }),
        );
        this.#universeId.value = adapter.getState().universeId;

        // Use filter signal directly from StickyFilterSlice eventBus
        this.#filterSignal = stickyFilterEventBus.getSignal('widget::stickyFilterValue');
    }

    #setupColumnTemplateQuery(key: KnownColumnTemplateKey): void {
        const queryOptions = computed(() => {
            const incoming = this.#incomingColumnTemplate.value;
            if (incoming === null) {
                return getDefaultColumnTemplateQuery(key);
            } else if (typeof incoming === 'string') {
                return getColumnTemplateByIdQuery(incoming);
            } else {
                return {
                    queryKey: ['concrete-template', 'disabled'] as const,
                    queryFn: async () => incoming,
                    enabled: false,
                };
            }
        });

        this.#columnTemplateQuery = this.#queryObserverModelFactory(queryOptions);
        this.#disposableStack.use(this.#columnTemplateQuery);

        this.#disposableStack.defer(
            effect(() => {
                const incoming = this.#incomingColumnTemplate.value;
                if (incoming !== null && typeof incoming !== 'string') {
                    this.#columnTemplate.value = generateWorkableColumnTemplateGridSettings(incoming);
                } else if (this.#columnTemplateQuery) {
                    const result = this.#columnTemplateQuery.signal.value;
                    if (result.data) {
                        this.#columnTemplate.value = generateWorkableColumnTemplateGridSettings(result.data);
                    }
                    if (result.error) {
                        this.#error.value = result.error as Error;
                        this.#status.value = 'error';
                    }
                }
            }),
        );
    }

    #setupStandardSlowIdeasLoading(slowIdeasSelector: SlowIdeasSelector): void {
        // In standard mode, the widget model manages the slow idea directly via
        // slowIdeasSelector.setSlowIdea(). Derive #slowIdea and its cache key
        // from the selector's signal.
        const slowIdeaSignal = slowIdeasSelector.eventBus.getSignal('widget::slowIdea');
        let cacheKeyCounter = 0;

        this.#slowIdea = computed(() => slowIdeaSignal.value ?? null);
        // Track the slug-map signal too — when subscriptions change and the
        // slug map refetches, the cache key bumps so the table query re-runs
        // with the populated map without needing a page refresh.
        this.#slowIdeaCacheKey = computed(() => {
            void this.#slowIdea.value;
            void this.#slugMapService.slugMap.value;
            return String(cacheKeyCounter++);
        });
    }

    #setupUniverseResolvedSlowIdeaLoading(slowIdeasSelector: SlowIdeasSelector): void {
        const slowIdea = signal<IdeaDTO | null>(null);
        const slowIdeaCacheKey = signal<string>(v4());
        this.#slowIdea = slowIdea;
        this.#slowIdeaCacheKey = slowIdeaCacheKey;

        // 1. Setup universe query to determine if an ID is a universe
        this.#universesQuery = this.#queryObserverModelFactory(computed(() => getUniversesQuery()));
        this.#disposableStack.use(this.#universesQuery);

        // 2. Computed: determine if the selected ID is a universe or an idea
        const isIdeaIdSignal = computed(() => {
            const selectedId = this.#universeId.value;
            const universes = this.#universesQuery?.signal.value.data;
            const isUniversesLoading = this.#universesQuery?.signal.value.isPending ?? true;

            if (!selectedId || isUniversesLoading) return false;
            return universes?.every((u) => u.id !== selectedId) ?? false;
        });

        // 3. Setup slow idea query (only enabled when we have an idea ID, not a universe)
        const slowIdeaQueryOptions = computed(() => ({
            ...getIdeaDTOByIdQuery(
                this.#queryClient,
                this.#formulaService,
                this.#slugMapService,
                this.#universeId.value!,
            ),
            enabled: isIdeaIdSignal.value && Boolean(this.#universeId.value),
        }));

        this.#slowIdeaQuery = this.#queryObserverModelFactory(slowIdeaQueryOptions);
        this.#disposableStack.use(this.#slowIdeaQuery);

        // 4. Effect: update slowIdeasSelector with the loaded idea DTO
        this.#disposableStack.defer(
            effect(() => {
                const selectedId = this.#universeId.value;
                const isIdeaId = isIdeaIdSignal.value;
                const isUniversesLoading = this.#universesQuery?.signal.value.isPending ?? true;
                const ideaQueryResult = this.#slowIdeaQuery?.signal.value;

                untracked(() => {
                    if (!selectedId) {
                        slowIdeasSelector.setSlowIdea(null);
                        slowIdea.value = null;
                        slowIdeaCacheKey.value = v4();
                        return;
                    }

                    if (isUniversesLoading) {
                        return;
                    }

                    let slowIdeaDTO: IdeaDTO | null = null;

                    if (isIdeaId) {
                        if (ideaQueryResult?.data) {
                            slowIdeaDTO = ideaQueryResult.data;
                        }
                    } else {
                        slowIdeaDTO = UniversePlanDTO.fromUniverseId(selectedId).toTempIdea();
                    }

                    if (slowIdeaDTO) {
                        slowIdeasSelector.setSlowIdea(slowIdeaDTO);
                        if (slowIdea.value !== slowIdeaDTO) {
                            slowIdea.value = slowIdeaDTO;
                            slowIdeaCacheKey.value = v4();
                        }
                    }
                });
            }),
        );

        // Re-bump the slow-idea cache key whenever the slug-map signal emits
        // (e.g. after a subscription change). Skip the first emission so we
        // don't double-bump on initial load.
        let slugMapSeen = false;
        this.#disposableStack.defer(
            effect(() => {
                void this.#slugMapService.slugMap.value;
                if (!slugMapSeen) {
                    slugMapSeen = true;
                    return;
                }
                slowIdeaCacheKey.value = v4();
            }),
        );
    }

    #setupTableQuery(): void {
        this.#disposableStack.defer(
            effect(() => {
                const newSort = clientSortModelToSortModel(this.#clientSortModel.value, this.#scrollingEnabled.value);
                if (!isEqual(newSort, this.#sort.value)) {
                    this.#sort.value = newSort;
                }
            }),
        );

        const columnOptions = this.#columnOptionsEventBus.getSignal('widget::columnOptions');
        // Client-stitched columns (ADR 0020): a stage between the column template and the table.
        const stitched =
            this.#stitchedColumnsFactory &&
            this.#disposableStack.use(
                new SymbolTableStitching({
                    stitch: this.#featureFlagService.getSignal(enableStitchedSymbolTables),
                    createColumns: this.#stitchedColumnsFactory,
                    slugMap: this.#slugMapService.slugMap,
                    columnTemplate: this.#columnTemplate,
                    sort: this.#sort,
                    searching: computed(() => this.#filterSignal.value !== ''),
                    filteredColumnIds: this.columnFilters.appliedColumnIds,
                    columnOptions,
                    devtoolsName: this.#devtoolsName ?? 'ResultsAreaSlice/table',
                }),
            );
        this.#stitched.value = stitched ?? null;
        const tableTemplate = stitched?.tableTemplate ?? this.#columnTemplate;
        const columnTemplateRunKey = computed(() => makeColumnTemplateEqualityKey(tableTemplate.value ?? undefined));

        const tableQueryOptions = computed(() => {
            const columnTemplate = tableTemplate.value;
            const enabled = Boolean(columnTemplate);

            if (TRACE) {
                if (this.#prevEnabled !== enabled) {
                    this.#log.trace({ message: 'Query enabled?', enabled });
                    this.#prevEnabled = enabled;
                }
            }

            if (!columnTemplate) {
                return {
                    queryKey: ['noop'] as const,
                    queryFn: async () => [null, []] as [null, OutputColumnWithMetadata[]],
                    enabled: false,
                };
            }

            return this.#buildQueryOptions(columnTemplate, columnTemplateRunKey.value, this.#userViewKeySignal.value);
        });

        const tableOnlyQueryOptions = computed(() => {
            const opts = tableQueryOptions.value;
            return {
                ...opts,
                select: (data: [any, OutputColumnWithMetadata[]]) => {
                    if (data[1]) {
                        this.#validationColumns.value = data[1];
                    }
                    return data[0];
                },
            };
        });

        this.#table.init({
            queryOptions: tableOnlyQueryOptions,
            initBounds: { firstRow: 0, windowSize: 100 },
            devtoolsName: this.#devtoolsName,
        });

        this.#disposableStack.use(this.#table);

        this.#disposableStack.defer(
            effect(() => {
                const status = this.#table.connectionStatus.value;
                const failureCount = this.#table.failureCount.value;

                if (status === 'connected') {
                    this.#status.value = 'ready';
                } else if (failureCount > 0 && this.#table.error) {
                    this.#status.value = 'error';
                    this.#error.value = this.#table.error;
                }
            }),
        );
    }

    // Reads every input but the template and the view key, which the live table and an export's
    // full table supply separately.
    #buildQueryOptions(
        columnTemplate: Pick<ColumnTemplate, 'columns'>,
        runKey: string | undefined,
        userViewKey: string | undefined,
    ): ReturnType<typeof buildResultsTableQueryOptions> {
        const filter = searchWithColumnFilters(
            this.#filterSignal.value,
            this.columnFilters.formula.value,
            this.#filterableColumns.value.keys(),
        );

        // Determine fast/slow ideas based on mode
        let fastIdeas: IdeaDTO[];
        let slowIdea: IdeaDTO | undefined;
        let fastIdeasCacheKey: string;
        let slowIdeaCacheKey: string;

        if (this.#ideaMode === 'screener' || this.#ideaMode === 'watchlist') {
            slowIdea = this.#widgetPlanIdea.value ?? undefined;
            fastIdeas = [];
            fastIdeasCacheKey = this.#widgetPlanCacheKey.value;
            slowIdeaCacheKey = this.#widgetPlanCacheKey.value;
        } else if (this.#ideaMode === 'scanner') {
            fastIdeas = this.#widgetPlanIdea.value ? [this.#widgetPlanIdea.value] : [];
            slowIdea = this.#slowIdea.value ?? undefined;
            fastIdeasCacheKey = this.#widgetPlanCacheKey.value;
            slowIdeaCacheKey = this.#slowIdeaCacheKey.value;
        } else {
            // standard mode
            fastIdeas = this.#fastIdeas.value;
            slowIdea = this.#slowIdea.value ?? undefined;
            fastIdeasCacheKey = this.#fastIdeasCacheKey.value;
            slowIdeaCacheKey = this.#slowIdeaCacheKey.value;
        }

        const fastIdeaDwellTime =
            this.#scrollingEnabled.value && this.#dwellTimeSignal.value > 0
                ? Duration.ofSeconds(this.#dwellTimeSignal.value)
                : undefined;

        return buildResultsTableQueryOptions({
            slowIdea,
            fastIdeas,
            validationModeEnabled: this.#validationModeEnabled.value,
            columnTemplate,
            useScrolling: this.#scrollingEnabled.value,
            filter,
            userViewKey,
            ideaUserViewKeyScope: this.#ideaUserViewKeyScope,
            fastIdeaDwellTime,
            columnOptions: this.#columnOptionsEventBus.getSignal('widget::columnOptions').value,
            log: this.#log,
            fastIdeasCacheKey,
            slowIdeaCacheKey,
            columnTemplateRunKey: runKey,
            sort: this.#sort.value,
        });
    }

    async exportCSV(columnNames: Record<string, string>): Promise<ExportTableCSVResult> {
        return await exportWithToasts(this.#toasts, t, () => this.#runExportCSV(columnNames));
    }

    async #runExportCSV(columnNames: Record<string, string>): Promise<ExportTableCSVResult> {
        const columnTemplate = this.#columnTemplate.value;
        // A stitched table computes only its keys columns, so a stitched export snapshots a full
        // table. A scrolling table is a log: a new one starts without the live log's history, so
        // its export snapshots the live table, keys columns only (ADR 0020, OD-EXPORT-001).
        if (this.#stitching.value === undefined || !columnTemplate || this.#table.isScrolling.value) {
            return await exportTableToCSV(this.#table, columnNames, t);
        }
        // A view key of its own: a stable key lets the server amend that view in place, and the
        // live keys table must keep its columns (CN-IMPL-004).
        const options = untracked(() =>
            this.#buildQueryOptions(columnTemplate, makeColumnTemplateEqualityKey(columnTemplate), v4()),
        );
        const fullTableExport = new FullTableExport({
            createTable: this.#createTable,
            queryOptions: {
                ...options,
                select: (data: [FetchableTableCreationResultWithColumns | null, OutputColumnWithMetadata[]]) => data[0],
            },
            columnNames,
            t,
            logger: this.#log,
            devtoolsName: this.#devtoolsName ?? 'ResultsAreaSlice/table',
        });
        this.#fullTableExport.next(fullTableExport);
        return await fullTableExport.result;
    }

    [Symbol.dispose](): void {
        this.#disposableStack.dispose();
    }
}
